import React from 'react';
import type { SeverityToken } from '../types/security';

interface SeverityBadgeProps {
  /** Token in {critical, high, medium, low, informational}; null → informational fallback. */
  token: SeverityToken | string | null | undefined;
  /** Optional CVSS-style numeric score; rendered after the label if provided. */
  score?: number | null;
  /** When true and token is high/critical (or status='Exploited'), render
   * the ⚡ routing-eligible chip. Parent passes routing-eligibility derived
   * from charter-floor rule. */
  showRoutingEligibilityChip?: boolean;
}

const VALID_TOKENS: ReadonlySet<SeverityToken> = new Set([
  'critical',
  'high',
  'medium',
  'low',
  'informational',
]);

function normalize(token: SeverityBadgeProps['token']): SeverityToken {
  if (!token) return 'informational';
  const lower = String(token).toLowerCase();
  if (VALID_TOKENS.has(lower as SeverityToken)) {
    return lower as SeverityToken;
  }
  return 'informational';
}

export function SeverityBadge({
  token,
  score,
  showRoutingEligibilityChip = false,
}: SeverityBadgeProps): React.JSX.Element {
  const normalized = normalize(token);
  const scoreText = score != null && Number.isFinite(score)
    ? ` ${score.toFixed(1)}`
    : '';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span
        className="severity-badge"
        data-severity={normalized}
        aria-label={`Severity: ${normalized}${scoreText}`}
      >
        {normalized}
        {scoreText}
      </span>
      {showRoutingEligibilityChip && (
        <span
          className="routing-eligibility-chip"
          aria-label="Routing eligible"
          title="Charter-floor routing-eligibility overlay: this event qualifies for forward-wiring actions (severity critical/high or KEV-listed status='Exploited')."
        >
          ⚡ routing-eligible
        </span>
      )}
    </span>
  );
}
